import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PreviewIcon from "@mui/icons-material/Preview";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SchedulingAnchorConflicts from "./SchedulingAnchorConflicts";
import SchedulingDraftStatus from "./SchedulingDraftStatus";
import SchedulingIncidentSection from "./SchedulingIncidentSection";
import SchedulingResultsSection from "./SchedulingResultsSection";

export default function SchedulingReviewStep({
  anchorConflicts,
  canSchedule,
  configDirty,
  configSaved,
  fields,
  incidentError,
  incidentPins,
  intl,
  onClear,
  onEditMatch,
  onIncidentChange,
  onPreview,
  previewResult,
  proposalConflicts,
  scheduling,
  updatingPreparation,
}) {
  const hasDraft = Boolean(previewResult?.draft);
  const hasConflicts = anchorConflicts.length > 0 || proposalConflicts.length > 0;

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
            spacing={2}
          >
            <Box sx={{ minWidth: 0 }}>
              {hasDraft ? (
                <SchedulingDraftStatus
                  draft={previewResult.draft}
                  generatedAt={previewResult.calendar?.generated_at}
                  intl={intl}
                />
              ) : (
                <>
                  <Typography variant="h6">{intl.formatMessage({ id: "scheduling.review.noPreview" })}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "scheduling.review.generateHelp" })}
                  </Typography>
                </>
              )}
            </Box>
            <Tooltip title={!canSchedule ? intl.formatMessage({ id: "Completa los pasos anteriores primero" }) : ""}>
              <span>
                <Button
                  variant="contained"
                  startIcon={scheduling ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />}
                  onClick={() => onPreview()}
                  disabled={scheduling || !canSchedule}
                >
                  {intl.formatMessage({
                    id: hasDraft ? "scheduling.review.regenerate" : "scheduling.review.generate",
                  })}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {!canSchedule && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {!configSaved || configDirty
                ? intl.formatMessage({ id: "Guarda la configuración antes de calendarizar" })
                : intl.formatMessage({ id: "Agrega al menos una ventana de disponibilidad para calendarizar" })}
            </Alert>
          )}
          {canSchedule && !hasDraft && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {intl.formatMessage({ id: "scheduling.draft.required" })}
            </Alert>
          )}
          {hasDraft && (
            <Accordion disableGutters elevation={0} sx={{ mt: 2, border: "1px solid", borderColor: "divider" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  {intl.formatMessage({ id: "scheduling.draft.purposeTitle" })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "scheduling.draft.purpose" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "scheduling.draft.validity" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "scheduling.draft.regenerateGuidance" })}
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>

      {previewResult?.incident && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">{intl.formatMessage({ id: "scheduling.review.incidentPreview" })}</Typography>
          {previewResult.incident.reason} · {previewResult.incident.start} – {previewResult.incident.end} (
          {previewResult.incident.timezone}).{" "}
          {intl.formatMessage(
            { id: "scheduling.review.incidentScope" },
            { count: previewResult.incident.target_match_ids.length },
          )}
        </Alert>
      )}

      {previewResult && (
        <SchedulingResultsSection
          editingDisabled={scheduling || updatingPreparation}
          intl={intl}
          onEditMatch={onEditMatch}
          previewResult={previewResult}
        />
      )}

      {previewResult?.warnings?.length > 0 && (
        <SchedulingAnchorConflicts
          advisory
          proposal
          conflicts={previewResult.warnings}
          intl={intl}
          onEditMatch={onEditMatch}
          disabled={scheduling || updatingPreparation}
        />
      )}

      {hasConflicts && (
        <Accordion defaultExpanded sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <WarningAmberOutlinedIcon color="error" />
              <Box>
                <Typography variant="subtitle1">{intl.formatMessage({ id: "scheduling.review.conflicts" })}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: "scheduling.review.conflictsHelp" })}
                </Typography>
              </Box>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <SchedulingAnchorConflicts
              conflicts={anchorConflicts}
              intl={intl}
              onEditMatch={onEditMatch}
              disabled={scheduling || updatingPreparation}
            />
            <SchedulingAnchorConflicts
              proposal
              conflicts={proposalConflicts}
              intl={intl}
              onEditMatch={onEditMatch}
              disabled={scheduling || updatingPreparation}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle1">{intl.formatMessage({ id: "scheduling.review.operations" })}</Typography>
            <Typography variant="body2" color="text.secondary">
              {intl.formatMessage({ id: "scheduling.review.operationsHelp" })}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <SchedulingIncidentSection
            fields={fields}
            intl={intl}
            disabled={!canSchedule || updatingPreparation}
            busy={scheduling}
            pins={incidentPins}
            error={incidentError}
            onPreview={onPreview}
            onChange={onIncidentChange}
          />
          <Divider sx={{ my: 2 }} />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle1" color="error.main">
                {intl.formatMessage({ id: "scheduling.review.clear" })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: "scheduling.review.clearHelp" })}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={onClear}
              disabled={scheduling || updatingPreparation}
            >
              {intl.formatMessage({ id: "scheduling.review.clear" })}
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
